import { useState, useCallback, useEffect } from "react";
import {
  useAccount,
  useReadContract,
  useWriteContract,
  useWaitForTransactionReceipt,
} from "wagmi";
import {
  type Address,
  encodeAbiParameters,
  formatUnits,
} from "viem";
import { Loader2, ExternalLink, Plus } from "lucide-react";
import { ADDRESSES } from "../../config/contracts";
import {
  BIN_POSITION_MANAGER_ABI,
  BIN_POOL_MANAGER_ABI,
  BIN_STEPS,
  BIN_ACTIONS,
  sortCurrencies,
  binParameters,
  NO_HOOKS,
  type PoolKey,
} from "../../config/v4Contracts";
import { type TokenInfo, NATIVE_JASMY, DEFAULT_TOKEN_LIST } from "../../config/tokens";
import { TokenSelector } from "../swap/TokenSelector";
import { useTokenBalance } from "../../hooks/useTokenBalance";
import { useTokenAllowance } from "../../hooks/useTokenAllowance";
import {
  formatTokenAmount,
  getDeadline,
  parseTokenAmount,
  explorerUrl,
  cn,
} from "../../lib/utils";

// BIN price helper: binId → price (binStep in basis points)
function binIdToPrice(binId: number, binStep: number, decimals0: number, decimals1: number): number {
  // price = (1 + binStep/10000)^(binId - 2^23) adjusted for decimals
  const BASE = 1 + binStep / 10000;
  const REAL_SHIFT = 2 ** 23; // 8388608 — the center bin
  return Math.pow(BASE, binId - REAL_SHIFT) / Math.pow(10, decimals0 - decimals1);
}

type DistributionType = "spot" | "curve" | "bid-ask";

// Build deltaIds and distributions for a given distribution type
function buildDistribution(
  type: DistributionType,
  _activeBinId: number
): {
  deltaIds: number[];
  distributionX: bigint[];
  distributionY: bigint[];
} {
  const ONE = 10n ** 18n;

  if (type === "spot") {
    return {
      deltaIds: [0],
      distributionX: [ONE],
      distributionY: [ONE],
    };
  }

  if (type === "curve") {
    // Bell-curve across 11 bins: weights 1,2,4,8,16,24,16,8,4,2,1 (normalized)
    const BINS = 11;
    const half = Math.floor(BINS / 2);
    const rawWeights = [1, 2, 4, 8, 16, 24, 16, 8, 4, 2, 1];
    const totalWeight = rawWeights.reduce((a, b) => a + b, 0);

    const deltaIds: number[] = [];
    const distributionX: bigint[] = [];
    const distributionY: bigint[] = [];

    for (let i = 0; i < BINS; i++) {
      const delta = i - half;
      deltaIds.push(delta);
      const weight = BigInt(Math.round((rawWeights[i] / totalWeight) * Number(ONE)));
      // bins below active: Y side, bins above: X side, center: both
      if (i < half) {
        distributionX.push(0n);
        distributionY.push(weight);
      } else if (i > half) {
        distributionX.push(weight);
        distributionY.push(0n);
      } else {
        const half_weight = weight / 2n;
        distributionX.push(half_weight);
        distributionY.push(weight - half_weight);
      }
    }

    return { deltaIds, distributionX, distributionY };
  }

  // bid-ask: 5 bins below (all Y) + 5 bins above (all X), center split
  const SIDE = 5;
  const deltaIds: number[] = [];
  const distributionX: bigint[] = [];
  const distributionY: bigint[] = [];
  const perBin = ONE / BigInt(SIDE * 2 + 1);

  for (let i = -SIDE; i <= SIDE; i++) {
    deltaIds.push(i);
    if (i < 0) {
      distributionX.push(0n);
      distributionY.push(perBin);
    } else if (i > 0) {
      distributionX.push(perBin);
      distributionY.push(0n);
    } else {
      distributionX.push(perBin / 2n);
      distributionY.push(perBin / 2n);
    }
  }

  return { deltaIds, distributionX, distributionY };
}

// Encode BIN_ADD_LIQUIDITY unlockData
function encodeBinAddLiquidityData(
  poolKey: PoolKey,
  deltaIds: number[],
  distributionX: bigint[],
  distributionY: bigint[],
  amountXDesired: bigint,
  amountYDesired: bigint,
  amountXMin: bigint,
  amountYMin: bigint,
  to: Address,
  deadline: bigint
): `0x${string}` {
  const actions = new Uint8Array([BIN_ACTIONS.BIN_ADD_LIQUIDITY]);
  const actionsHex = `0x${Array.from(actions).map((b) => b.toString(16).padStart(2, "0")).join("")}` as `0x${string}`;

  const params = encodeAbiParameters(
    [
      {
        name: "poolKey",
        type: "tuple" as const,
        components: [
          { name: "currency0", type: "address" as const },
          { name: "currency1", type: "address" as const },
          { name: "hooks", type: "address" as const },
          { name: "poolManager", type: "address" as const },
          { name: "fee", type: "uint24" as const },
          { name: "parameters", type: "bytes32" as const },
        ] as const,
      },
      { name: "deltaIds", type: "int256[]" as const },
      { name: "distributionX", type: "uint256[]" as const },
      { name: "distributionY", type: "uint256[]" as const },
      { name: "amountXDesired", type: "uint256" as const },
      { name: "amountYDesired", type: "uint256" as const },
      { name: "amountXMin", type: "uint256" as const },
      { name: "amountYMin", type: "uint256" as const },
      { name: "to", type: "address" as const },
      { name: "deadline", type: "uint256" as const },
    ] as const,
    [
      {
        currency0: poolKey.currency0,
        currency1: poolKey.currency1,
        hooks: poolKey.hooks,
        poolManager: poolKey.poolManager,
        fee: poolKey.fee,
        parameters: poolKey.parameters,
      },
      deltaIds.map(BigInt),
      distributionX,
      distributionY,
      amountXDesired,
      amountYDesired,
      amountXMin,
      amountYMin,
      to,
      deadline,
    ]
  );

  return encodeAbiParameters(
    [
      { name: "actions", type: "bytes" },
      { name: "params", type: "bytes[]" },
    ] as const,
    [actionsHex, [params]]
  );
}

export function BinLiquidityCard() {
  const { address: account, isConnected } = useAccount();

  const [tokenA, setTokenA] = useState<TokenInfo>(NATIVE_JASMY);
  const [tokenB, setTokenB] = useState<TokenInfo>(DEFAULT_TOKEN_LIST[1]);
  const [selectedBinIdx, setSelectedBinIdx] = useState(1); // default 0.25%
  const [distribution, setDistribution] = useState<DistributionType>("spot");
  const [amountA, setAmountA] = useState("");
  const [amountB, setAmountB] = useState("");
  const [txHash, setTxHash] = useState<`0x${string}` | undefined>();

  const binStep = BIN_STEPS[selectedBinIdx];

  const addrA: Address = tokenA.isNative ? ADDRESSES.WJASMY : tokenA.address;
  const addrB: Address = tokenB.isNative ? ADDRESSES.WJASMY : tokenB.address;
  const [currency0, currency1] = sortCurrencies(addrA, addrB);
  const isToken0A = addrA.toLowerCase() === currency0.toLowerCase();

  const decimals0 = isToken0A ? tokenA.decimals : tokenB.decimals;
  const decimals1 = isToken0A ? tokenB.decimals : tokenA.decimals;

  const poolKey: PoolKey = {
    currency0,
    currency1,
    hooks: NO_HOOKS,
    poolManager: ADDRESSES.BIN_POOL_MANAGER,
    fee: 0,
    parameters: binParameters(binStep.binStep),
  };

  // Read active bin ID
  const poolIdBytes = `0x${currency0.slice(2)}${currency1.slice(2)}${binStep.binStep.toString(16).padStart(6, "0")}` as `0x${string}`;
  const { data: activeBinData } = useReadContract({
    address: ADDRESSES.BIN_POOL_MANAGER,
    abi: BIN_POOL_MANAGER_ABI,
    functionName: "getActiveId",
    args: [poolIdBytes],
    query: { refetchInterval: 10_000 },
  });
  const activeBinId = activeBinData ? Number(activeBinData as bigint) : 0;
  const currentPrice = activeBinId > 0
    ? binIdToPrice(activeBinId, binStep.binStep, decimals0, decimals1)
    : 0;

  const { balance: balanceA } = useTokenBalance(tokenA.isNative ? undefined : tokenA.address);
  const { balance: balanceB } = useTokenBalance(tokenB.isNative ? undefined : tokenB.address);

  const parsedA = parseTokenAmount(amountA, tokenA.decimals);
  const parsedB = parseTokenAmount(amountB, tokenB.decimals);

  // Map to X/Y (currency0/currency1)
  const amountXDesired = isToken0A ? parsedA : parsedB;
  const amountYDesired = isToken0A ? parsedB : parsedA;
  const amountXMin = (amountXDesired * 95n) / 100n;
  const amountYMin = (amountYDesired * 95n) / 100n;

  // Build distribution arrays
  const dist = buildDistribution(distribution, activeBinId);

  // Approvals
  const { needsApproval: needsApprovalA, approve: approveA, isApproving: isApprovingA } =
    useTokenAllowance(
      tokenA.isNative ? undefined : tokenA.address,
      ADDRESSES.BIN_POOL_MANAGER,
      parsedA
    );
  const { needsApproval: needsApprovalB, approve: approveB, isApproving: isApprovingB } =
    useTokenAllowance(
      tokenB.isNative ? undefined : tokenB.address,
      ADDRESSES.BIN_POOL_MANAGER,
      parsedB
    );

  const { writeContract, isPending } = useWriteContract();
  const { isLoading: isConfirming, isSuccess } = useWaitForTransactionReceipt({
    hash: txHash,
  });

  useEffect(() => {
    if (isSuccess) {
      setAmountA("");
      setAmountB("");
      setTxHash(undefined);
    }
  }, [isSuccess]);

  const addLiquidity = useCallback(() => {
    if (!account || (amountXDesired === 0n && amountYDesired === 0n)) return;
    const deadline = BigInt(getDeadline(20));

    const unlockData = encodeBinAddLiquidityData(
      poolKey,
      dist.deltaIds,
      dist.distributionX,
      dist.distributionY,
      amountXDesired,
      amountYDesired,
      amountXMin,
      amountYMin,
      account,
      deadline
    );

    const nativeValue = tokenA.isNative ? parsedA : tokenB.isNative ? parsedB : 0n;

    writeContract(
      {
        address: ADDRESSES.BIN_POOL_MANAGER,
        abi: BIN_POSITION_MANAGER_ABI,
        functionName: "modifyLiquidities",
        args: [unlockData, deadline],
        value: nativeValue,
      },
      { onSuccess: (h) => setTxHash(h) }
    );
  }, [
    account,
    poolKey,
    dist,
    amountXDesired,
    amountYDesired,
    amountXMin,
    amountYMin,
    tokenA,
    tokenB,
    parsedA,
    parsedB,
    writeContract,
  ]);

  const isLoading = isPending || isConfirming;

  function getButtonText(): string {
    if (!isConnected) return "Connect Wallet";
    if (!amountA || parseFloat(amountA) <= 0) return "Enter Amount";
    if (activeBinId === 0) return "Pool Not Initialized";
    if (isApprovingA) return `Approving ${tokenA.symbol}...`;
    if (needsApprovalA) return `Approve ${tokenA.symbol}`;
    if (isApprovingB) return `Approving ${tokenB.symbol}...`;
    if (needsApprovalB) return `Approve ${tokenB.symbol}`;
    if (isPending) return "Confirming...";
    if (isConfirming) return "Adding Liquidity...";
    return "Add Bin Liquidity";
  }

  function handleAction() {
    if (needsApprovalA) return approveA();
    if (needsApprovalB) return approveB();
    addLiquidity();
  }

  return (
    <div className="glass-card p-6 w-full max-w-[540px]">
      <div className="mb-5">
        <h2 className="font-display font-semibold text-xl mb-1">Bin Liquidity</h2>
        <p className="text-text-tertiary text-xs">
          Liquidity Book pools provide extreme precision. Each bin is a constant-price market.
        </p>
      </div>

      {/* Token pair */}
      <div className="flex items-center gap-3 mb-5">
        <div className="flex-1">
          <div className="text-xs text-text-tertiary mb-1.5">Token A</div>
          <TokenSelector selectedToken={tokenA} onSelect={setTokenA} otherToken={tokenB} />
        </div>
        <Plus className="w-4 h-4 text-text-tertiary mt-5 flex-shrink-0" />
        <div className="flex-1">
          <div className="text-xs text-text-tertiary mb-1.5">Token B</div>
          <TokenSelector selectedToken={tokenB} onSelect={setTokenB} otherToken={tokenA} />
        </div>
      </div>

      {/* Bin step selector */}
      <div className="mb-5">
        <div className="text-xs text-text-tertiary mb-2">Bin Step</div>
        <div className="grid grid-cols-4 gap-1.5">
          {BIN_STEPS.map((step, i) => (
            <button
              key={step.binStep}
              onClick={() => setSelectedBinIdx(i)}
              className={cn(
                "flex flex-col items-center py-2 px-1 rounded-lg border text-xs font-medium transition-all",
                selectedBinIdx === i
                  ? "bg-primary/15 border-primary/30 text-primary"
                  : "bg-surface border-border text-text-secondary hover:text-text-primary hover:bg-surface-hover"
              )}
            >
              <span className="font-semibold">{step.label}</span>
              <span className="text-text-tertiary text-[10px] mt-0.5">
                step {step.binStep}
              </span>
            </button>
          ))}
        </div>
      </div>

      {/* Pool info */}
      {activeBinId > 0 && (
        <div className="mb-4 p-3 bg-surface/30 rounded-button border border-border text-sm">
          <div className="flex items-center justify-between text-text-secondary">
            <span>Active Bin</span>
            <span className="font-medium text-text-primary">{activeBinId}</span>
          </div>
          <div className="flex items-center justify-between text-text-secondary mt-1">
            <span>Current Price</span>
            <span>
              {currentPrice > 0
                ? `${currentPrice.toFixed(6)} ${isToken0A ? tokenB.symbol : tokenA.symbol} / ${isToken0A ? tokenA.symbol : tokenB.symbol}`
                : "—"}
            </span>
          </div>
        </div>
      )}

      {/* Distribution type */}
      <div className="mb-5">
        <div className="text-xs text-text-tertiary mb-2">Distribution Shape</div>
        <div className="grid grid-cols-3 gap-1.5">
          {(["spot", "curve", "bid-ask"] as DistributionType[]).map((type) => (
            <button
              key={type}
              onClick={() => setDistribution(type)}
              className={cn(
                "flex flex-col items-center py-2 px-1 rounded-lg border text-xs font-medium transition-all capitalize",
                distribution === type
                  ? "bg-primary/15 border-primary/30 text-primary"
                  : "bg-surface border-border text-text-secondary hover:text-text-primary hover:bg-surface-hover"
              )}
            >
              {type}
            </button>
          ))}
        </div>
        <div className="mt-2 text-xs text-text-tertiary">
          {distribution === "spot" && "All liquidity in the current active bin."}
          {distribution === "curve" && "Bell-curve spread across 11 bins. Lower slippage on balanced swaps."}
          {distribution === "bid-ask" && "50% below active bin (Y), 50% above (X). Mimics an order book."}
        </div>
      </div>

      {/* Amount inputs */}
      <div className="space-y-3 mb-5">
        <div className="token-input-row">
          <div className="flex items-center justify-between mb-2">
            <span className="text-xs text-text-tertiary">{tokenA.symbol} Amount</span>
            <button
              onClick={() => setAmountA(formatUnits(balanceA, tokenA.decimals))}
              className="text-xs text-text-tertiary hover:text-primary transition-colors"
            >
              Balance: {formatTokenAmount(balanceA, tokenA.decimals)}
              <span className="ml-1 text-primary font-medium">MAX</span>
            </button>
          </div>
          <input
            type="number"
            value={amountA}
            onChange={(e) => setAmountA(e.target.value)}
            placeholder="0.0"
            className="amount-input"
            min="0"
            step="any"
          />
        </div>

        <div className="flex justify-center">
          <Plus className="w-4 h-4 text-text-tertiary" />
        </div>

        <div className="token-input-row">
          <div className="flex items-center justify-between mb-2">
            <span className="text-xs text-text-tertiary">{tokenB.symbol} Amount</span>
            <button
              onClick={() => setAmountB(formatUnits(balanceB, tokenB.decimals))}
              className="text-xs text-text-tertiary hover:text-primary transition-colors"
            >
              Balance: {formatTokenAmount(balanceB, tokenB.decimals)}
              <span className="ml-1 text-primary font-medium">MAX</span>
            </button>
          </div>
          <input
            type="number"
            value={amountB}
            onChange={(e) => setAmountB(e.target.value)}
            placeholder="0.0"
            className="amount-input"
            min="0"
            step="any"
          />
        </div>
      </div>

      {/* Bin distribution preview */}
      {amountA && parseFloat(amountA) > 0 && (
        <div className="mb-4 p-3 bg-surface/30 rounded-button border border-border text-sm">
          <div className="text-text-tertiary text-xs mb-2">Bin Distribution Preview</div>
          <div className="flex justify-between text-text-secondary py-0.5">
            <span>Bins Used</span>
            <span>{dist.deltaIds.length}</span>
          </div>
          <div className="flex justify-between text-text-secondary py-0.5">
            <span>Shape</span>
            <span className="capitalize">{distribution}</span>
          </div>
          <div className="flex justify-between text-text-secondary py-0.5">
            <span>Slippage Protection</span>
            <span>5%</span>
          </div>
        </div>
      )}

      <button
        onClick={handleAction}
        disabled={
          !isConnected ||
          isLoading ||
          isApprovingA ||
          isApprovingB ||
          activeBinId === 0 ||
          (!needsApprovalA && !needsApprovalB && parsedA === 0n && parsedB === 0n)
        }
        className="btn-primary w-full flex items-center justify-center gap-2"
      >
        {(isLoading || isApprovingA || isApprovingB) && (
          <Loader2 className="w-4 h-4 animate-spin" />
        )}
        {getButtonText()}
      </button>

      {txHash && (
        <div className="mt-3 text-center">
          <a
            href={explorerUrl("tx", txHash)}
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-1 text-sm text-primary hover:text-primary-hover transition-colors"
          >
            View Transaction <ExternalLink className="w-3 h-3" />
          </a>
        </div>
      )}
    </div>
  );
}
